export const HELP_CONTENT = {
  version: "1.0.0",
  updatedAt: "2025-09-15T00:00:00.000Z",
  product: "Santé",
  pages: [
    {
      id: "algorithms-create",
      routeMatch: ["/algoritmos", "/algoritmos/:id/adicionarAlgoritmo", "/algoritmos/:id/editarAlgoritmo"],
      title: "Algoritmo de Pontuação — Criar/Editar",
      audience: ["Admin", "Conteudista"],
      summary:
        "Defina como cada pergunta do teste distribui pontos para os perfis (A, C, O, I). Suporta perguntas fechadas (escolha única) e de prioridade (ordenação), com pesos e combinações avançadas.",
      quickLinks: [
        { label: "Criar novo algoritmo", anchor: "#passo-a-passo" },
        { label: "Tipos de pergunta", anchor: "#tipos-de-pergunta" },
        { label: "Mapeamento A→Perfis", anchor: "#mapeamento-por-alternativa" },
        { label: "Pesos e prioridade", anchor: "#pesos" },
        { label: "Publicar/Salvar", anchor: "#salvar-publicar" },
        { label: "FAQ", anchor: "#faq" }
      ],
      sections: [
        {
          id: "visao-geral",
          title: "Visão geral",
          blocks: [
            {
              type: "text",
              text:
                "Nesta página você descreve o algoritmo que converte respostas em pontuação por perfil. O resultado final é consolidado em percentuais para A, C, O e I."
            },
            {
              type: "list",
              style: "bulleted",
              items: [
                "Perfis do teste: A, C, O, I (configuráveis no JSON).",
                "Perguntas podem ser *Fechadas* (A–D, escolha única) ou de *Prioridade* (ordenar alternativas).",
                "Cada alternativa pode dar pontos a um ou mais perfis, com pesos.",
                "Ferramentas avançadas: combinações (tuples) e exclusões (desligadas por padrão)."
              ]
            },
            {
              type: "callout",
              variant: "info",
              text:
                "A referência de cada pergunta pode ser por texto (by: 'text') ou por id estável (by: 'id'). Preferimos id para evitar que mudanças de texto quebrem o vínculo."
            }
          ]
        },

        {
          id: "passo-a-passo",
          title: "Passo a passo",
          blocks: [
            {
              type: "steps",
              steps: [
                {
                  title: "Preencha os metadados",
                  detail:
                    "Informe título, selecione o teste de destino e confirme os perfis usados (A/C/O/I)."
                },
                {
                  title: "Adicione perguntas",
                  detail:
                    "Para cada pergunta, escolha o tipo: *Fechada* (A–D) ou *Prioridade*. O texto da pergunta vira a referência se você usar `by: 'text'`."
                },
                {
                  title: "Mapeie alternativas → perfis",
                  detail:
                    "Na tabela, para a alternativa (A, B, C, D), use **Selecionar → Adicionar** para listar os perfis que recebem pontos quando essa alternativa for escolhida."
                },
                {
                  title: "Ajuste os pesos",
                  detail:
                    "Pergunta *Fechada*: defina `Peso ao selecionar` (padrão 1) e, opcionalmente, sobrescreva por alternativa.\nPergunta *Prioridade*: defina os **Pesos por Posição** (ex.: 4,3,2,1)."
                },
                {
                  title: "Revise e salve",
                  detail:
                    "Use o botão Salvar. O JSON é gerado com `questions[]`, `optionToProfiles`, `closedScoring` e, se aplicável, `priorityScoring`."
                },
                {
                  title: "Publicar (opcional)",
                  detail:
                    "Se sua aplicação diferencia rascunho/publicado, publique o algoritmo para entrar em produção."
                }
              ]
            },
            {
              type: "callout",
              variant: "tip",
              text:
                "Comece simples: pesos = 1 e um único perfil por alternativa. Depois evolua para pesos diferentes ou múltiplos perfis por alternativa."
            }
          ]
        },

        {
          id: "tipos-de-pergunta",
          title: "Tipos de pergunta",
          blocks: [
            {
              type: "list",
              style: "cards",
              items: [
                {
                  title: "Fechada (A–D, escolha única)",
                  body:
                    "O participante escolhe **uma** letra entre A, B, C, D. A alternativa escolhida distribui pontos para os perfis listados em `optionToProfiles[letra]`, ponderados pelo peso efetivo."
                },
                {
                  title: "Prioridade (ordenação)",
                  body:
                    "O participante ordena as alternativas. Cada posição tem um peso (ex.: 1º=4, 2º=3, 3º=2, 4º=1). A pontuação de cada alternativa é dada pelo peso da posição em que foi colocada."
                }
              ]
            },
            {
              type: "code",
              lang: "js",
              code: `// Cálculo efetivo por tipo
// Fechada:
const pesoEfetivoFechada = (optionWeights[letra] ?? selectedWeight);

// Prioridade:
const pontosDaAlternativa = pesosPorPosicao[posicaoEscolhida]; // ex.: [4,3,2,1]`
            },
            {
              type: "callout",
              variant: "info",
              text:
                "Ambos os tipos usam o mesmo mapeamento Alternativa → Perfis. A diferença está em como a alternativa acumula pontos (peso único x peso por posição)."
            }
          ]
        },

        {
          id: "mapeamento-por-alternativa",
          title: "Mapeamento por Alternativa",
          blocks: [
            {
              type: "text",
              text:
                "Cada linha representa uma alternativa (A, B, C, D). Para cada linha, selecione um perfil na lista e clique em **Adicionar** para que ele passe a receber pontos sempre que essa alternativa for escolhida."
            },
            {
              type: "list",
              style: "bulleted",
              items: [
                "Você pode adicionar **vários perfis** à mesma alternativa (ex.: B → [A, O]).",
                "Remova um perfil do chip para que ele pare de receber pontos daquela alternativa.",
                "As colunas da direita mostram os perfis que receberão pontos quando aquela alternativa for escolhida."
              ]
            },
            {
              type: "callout",
              variant: "warning",
              text:
                "Se nenhuma alternativa tiver perfis mapeados, o teste resultará em 0 pontos para todos os perfis."
            }
          ]
        },

        {
          id: "pesos",
          title: "Configuração de pesos",
          blocks: [
            {
              type: "list",
              style: "numbered",
              items: [
                "**Fechada** — `Peso ao selecionar` (padrão 1): peso base aplicado à alternativa escolhida.",
                "**Fechada** — `Peso por Alternativa (coluna)`: sobrescreve o peso base para uma letra específica.",
                "**Prioridade** — `Pesos por Posição`: uma lista com o peso do 1º lugar, 2º, 3º, 4º... Deve ter o mesmo tamanho do número de alternativas."
              ]
            },
            {
              type: "code",
              lang: "json",
              code: `{
  "type": "closed",
  "optionToProfiles": { "A": ["A"], "B": ["C"], "C": ["O"], "D": ["I"] },
  "closedScoring": {
    "selectedWeight": 1,
    "optionWeights": { "A": 1, "B": 1, "C": 1, "D": 1 }
  }
}
// Prioridade (exemplo de formato esperado pela página):
{
  "type": "priority",
  "optionToProfiles": { "A": ["A"], "B": ["C"], "C": ["O"], "D": ["I"] },
  "priorityScoring": {
    "positionWeights": [4,3,2,1] // 1º→4, 2º→3, 3º→2, 4º→1
  }
}`
            },
            {
              type: "callout",
              variant: "tip",
              text:
                "Padrão seguro: deixe todos os pesos em 1 para validar o mapeamento, depois ajuste finamente."
            }
          ]
        },

        {
          id: "salvar-publicar",
          title: "Salvar e publicar",
          blocks: [
            {
              type: "steps",
              steps: [
                {
                  title: "Salvar",
                  detail:
                    "Valida a estrutura, monta o JSON e persiste o algoritmo. Erros de validação apontam perguntas sem mapeamento/pesos inconsistentes."
                },
                {
                  title: "Publicar",
                  detail:
                    "Disponibiliza o algoritmo para ser usado pelo teste (se sua instalação tiver este fluxo)."
                }
              ]
            },
            {
              type: "callout",
              variant: "info",
              text:
                "Mudanças em algoritmos publicados podem afetar resultados em andamento. Prefira editar um rascunho e publicar uma nova versão."
            }
          ]
        },

        {
          id: "faq",
          title: "Perguntas frequentes",
          blocks: [
            {
              type: "qa",
              items: [
                {
                  q: "Preciso mapear todas as alternativas?",
                  a: "Sim. Se uma alternativa não tiver perfis associados, ela não pontua ninguém quando escolhida."
                },
                {
                  q: "Posso dar pontos para vários perfis com a mesma alternativa?",
                  a: "Sim. Basta adicionar múltiplos perfis na coluna 'Perfis que recebem'."
                },
                {
                  q: "Qual a diferença entre `selectedWeight` e `optionWeights`?",
                  a: "`selectedWeight` é o peso padrão; `optionWeights[letra]` sobrescreve esse valor para uma alternativa específica."
                },
                {
                  q: "Como funciona a pergunta de prioridade?",
                  a: "O usuário ordena A–D. A alternativa em 1º recebe `positionWeights[0]`, a de 2º recebe `positionWeights[1]`, e assim por diante. Depois, esse valor é creditado aos perfis mapeados naquela alternativa."
                },
                {
                  q: "O que são `tuples` e `exclusions`?",
                  a: "`tuples` permitem bônus/combos por padrões de respostas; `exclusions` removem combinações inválidas. Estão desligados por padrão."
                }
              ]
            }
          ]
        },

        {
          id: "troubleshooting",
          title: "Solução de problemas",
          blocks: [
            {
              type: "list",
              style: "bulleted",
              items: [
                "Resultados zerados: verifique se cada alternativa (A–D) tem ao menos um perfil mapeado.",
                "Percentuais não fecham 100%: confirme se houve ao menos uma resposta válida; em prioridade, confira o tamanho e valores de `positionWeights`.",
                "Pergunta não encontrada: se você usa `by: 'text'`, mudanças no enunciado podem quebrar a referência. Prefira `by: 'id'`."
              ]
            }
          ]
        },

        {
          id: "modelo-json",
          title: "Modelo de JSON gerado",
          blocks: [
            {
              type: "code",
              lang: "json",
              code: `{
  "meta": {
    "titulo": "Algoritmo de Pontuação",
    "testeId": "<id>",
    "testeNome": "Mapa Cerebral",
    "criadoEm": "2025-09-15T00:00:00.000Z"
  },
  "profiles": ["A", "C", "O", "I"],
  "questions": [
    {
      "ref": { "by": "text", "value": "1) Eu gosto de..." },
      "type": "closed",
      "optionToProfiles": { "A": ["A"], "B": ["C"], "C": ["O"], "D": ["I"] },
      "closedScoring": {
        "selectedWeight": 1,
        "optionWeights": { "A": 1, "B": 1, "C": 1, "D": 1 }
      }
    },
    {
      "ref": { "by": "text", "value": "2) ..." },
      "type": "priority",
      "optionToProfiles": { "A": ["A"], "B": ["C"], "C": ["O"], "D": ["I"] },
      "priorityScoring": { "positionWeights": [4,3,2,1] }
    }
  ],
  "tuples": [],
  "groups": [],
  "combinationPolicy": { "optionMapping": "on", "tuples": "off", "exclusions": [] }
}`
            }
          ]
        }
      ]
    },

    // Exemplos de slots prontos para outras páginas (você pode preencher depois):
    {
      id: "testes-list",
      routeMatch: ["/testes"],
      title: "Testes — Lista e Ações",
      audience: ["Admin"],
      summary: "Gerencie testes (criar, editar, publicar, arquivar).",
      sections: [
        {
          id: "em-breve",
          title: "Conteúdo em breve",
          blocks: [{ type: "text", text: "Esta ajuda será preenchida futuramente." }]
        }
      ]
    }
  ]
};

export default HELP_CONTENT;